import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  MapPin, 
  Truck, 
  Calendar, 
  CheckCircle2, 
  Circle, 
  ArrowRight,
  Navigation,
  Flag,
  Package,
  Clock,
  AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hitos del itinerario con sus etiquetas en español
const MILESTONE_LABELS = {
  'en_origen': { label: 'En Origen', icon: Package, description: 'Cargando en planta de origen' },
  'en_ruta_argentina': { label: 'En Ruta (Argentina)', icon: Navigation, description: 'Viajando por Argentina' },
  'en_frontera_argentina': { label: 'Frontera Argentina', icon: Flag, description: 'En paso fronterizo argentino' },
  'en_ruta_internacional': { label: 'Cruzando Frontera', icon: ArrowRight, description: 'Cruzando la frontera' },
  'en_frontera_chilena': { label: 'Frontera Chile', icon: Flag, description: 'En paso fronterizo chileno' },
  'en_puerto_terrestre': { label: 'Puerto Terrestre', icon: MapPin, description: 'En puerto terrestre (opcional)' },
  'en_ruta_chilena': { label: 'En Ruta (Chile)', icon: Navigation, description: 'Viajando por Chile' },
  'en_destino': { label: 'En Destino', icon: MapPin, description: 'Llegada a destino' },
  'descargado': { label: 'Descargado', icon: CheckCircle2, description: 'Descarga completada' }
};

const MILESTONE_ORDER = [
  'en_origen',
  'en_ruta_argentina',
  'en_frontera_argentina',
  'en_ruta_internacional',
  'en_frontera_chilena',
  'en_puerto_terrestre',
  'en_ruta_chilena',
  'en_destino',
  'descargado'
];

const DriverView = () => {
  const { user } = useAuth();
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItinerary, setSelectedItinerary] = useState(null);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchItineraries();
  }, []);

  const fetchItineraries = async () => {
    try {
      // El endpoint devuelve los itinerarios del chofer actual
      const response = await axios.get(`${API}/driver/itineraries`);
      setItineraries(response.data);
    } catch (error) {
      // Si no hay itinerarios o el endpoint no existe, mostrar mensaje
      if (error.response?.status !== 404) {
        toast.error('Error al cargar itinerarios');
      }
      setItineraries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMilestone = async () => {
    if (!selectedItinerary || !selectedMilestone) return;

    try {
      await axios.put(
        `${API}/itineraries/${selectedItinerary.id}/milestone/${selectedMilestone}`,
        { notes: notes.toUpperCase() }
      );
      toast.success('Estado actualizado correctamente');
      setUpdateModalOpen(false);
      setNotes('');
      fetchItineraries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar estado');
    }
  };

  const openUpdateModal = (itinerary, milestone) => {
    setSelectedItinerary(itinerary);
    setSelectedMilestone(milestone);
    setNotes('');
    setUpdateModalOpen(true);
  };

  const getCurrentMilestone = (milestones) => {
    if (!milestones) return null;
    // Encontrar el último hito completado
    for (let i = MILESTONE_ORDER.length - 1; i >= 0; i--) {
      const m = milestones.find(ms => ms.milestone === MILESTONE_ORDER[i]);
      if (m?.completed) {
        return MILESTONE_ORDER[i];
      }
    }
    return null;
  };

  const getNextMilestone = (milestones) => {
    if (!milestones) return MILESTONE_ORDER[0];
    const current = getCurrentMilestone(milestones);
    if (!current) return MILESTONE_ORDER[0];
    const currentIndex = MILESTONE_ORDER.indexOf(current);
    if (currentIndex < MILESTONE_ORDER.length - 1) {
      return MILESTONE_ORDER[currentIndex + 1];
    }
    return null;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Sin fecha';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('es-CL', { 
      day: '2-digit', 
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (user?.role !== 'CHOFER') {
    return (
      <Layout>
        <div className="text-center py-10">
          <AlertCircle size={48} className="mx-auto text-amber-500 mb-4" />
          <p className="text-slate-600">Esta vista es exclusiva para choferes</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="driver-view-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">Mi Viaje</h1>
            <p className="text-slate-600 mt-1">Actualiza el estado de tu recorrido</p>
          </div>
          <Button onClick={fetchItineraries} variant="outline" size="sm">
            Actualizar
          </Button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-300 border-t-slate-600"></div>
          </div>
        ) : itineraries.length === 0 ? (
          <Card className="bg-slate-50">
            <CardContent className="py-12 text-center">
              <Truck size={48} className="mx-auto text-slate-400 mb-4" />
              <p className="text-slate-600 text-lg">No tienes viajes asignados</p>
              <p className="text-slate-500 text-sm mt-2">Cuando te asignen un despacho, aparecerá aquí</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {itineraries.map((itinerary) => {
              const currentMilestone = getCurrentMilestone(itinerary.milestones);
              const nextMilestone = getNextMilestone(itinerary.milestones);
              const isCompleted = currentMilestone === 'descargado';

              return (
                <Card key={itinerary.id} className={`border-2 ${isCompleted ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200'}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Truck className={isCompleted ? 'text-emerald-600' : 'text-blue-600'} />
                        <span>Despacho #{itinerary.dispatch_number || 'N/A'}</span>
                      </div>
                      {isCompleted && (
                        <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                          Completado
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Info del viaje */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Package size={16} className="text-slate-500" />
                          <span className="text-slate-600">Origen:</span>
                          <span className="font-medium">{itinerary.direccion_carga || 'No especificado'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar size={16} className="text-slate-500" />
                          <span className="text-slate-600">Fecha Carga:</span>
                          <span className="font-medium">{formatDate(itinerary.fecha_carga)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Flag size={16} className="text-slate-500" />
                          <span className="text-slate-600">Paso Fronterizo:</span>
                          <span className="font-medium">{itinerary.paso_fronterizo || 'No especificado'}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin size={16} className="text-slate-500" />
                          <span className="text-slate-600">Destino:</span>
                          <span className="font-medium">{itinerary.direccion_descarga || 'No especificado'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar size={16} className="text-slate-500" />
                          <span className="text-slate-600">Fecha Descarga:</span>
                          <span className="font-medium">{formatDate(itinerary.fecha_descarga)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Timeline del itinerario */}
                    <div className="pt-2">
                      <h4 className="text-sm font-semibold text-slate-700 mb-4">Progreso del Viaje</h4>
                      <div className="relative">
                        {/* Línea de progreso */}
                        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200"></div>
                        
                        <div className="space-y-3">
                          {MILESTONE_ORDER.map((milestoneKey, index) => {
                            const milestone = itinerary.milestones?.find(m => m.milestone === milestoneKey);
                            const isComplete = milestone?.completed;
                            const isCurrent = milestoneKey === nextMilestone;
                            const info = MILESTONE_LABELS[milestoneKey];
                            const Icon = info.icon;

                            // Saltar puerto terrestre si no es necesario
                            if (milestoneKey === 'en_puerto_terrestre' && !milestone) {
                              return null;
                            }

                            return (
                              <div key={milestoneKey} className="relative flex items-start gap-4 pl-1">
                                {/* Indicador */}
                                <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                                  isComplete 
                                    ? 'bg-emerald-500 border-emerald-500 text-white' 
                                    : isCurrent 
                                      ? 'bg-blue-500 border-blue-500 text-white animate-pulse' 
                                      : 'bg-white border-slate-300 text-slate-400'
                                }`}>
                                  {isComplete ? (
                                    <CheckCircle2 size={16} />
                                  ) : (
                                    <Icon size={16} />
                                  )}
                                </div>

                                {/* Contenido */}
                                <div className={`flex-1 pb-3 ${index === MILESTONE_ORDER.length - 1 ? '' : 'border-b border-slate-100'}`}>
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className={`font-medium ${isComplete ? 'text-emerald-700' : isCurrent ? 'text-blue-700' : 'text-slate-600'}`}>
                                        {info.label}
                                      </p>
                                      <p className="text-xs text-slate-500">{info.description}</p>
                                      {isComplete && milestone?.completed_at && (
                                        <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                                          <Clock size={12} />
                                          {formatDateTime(milestone.completed_at)}
                                          {milestone.notes && ` - ${milestone.notes}`}
                                        </p>
                                      )}
                                    </div>
                                    
                                    {/* Botón de actualizar - solo para el siguiente hito */}
                                    {isCurrent && !isCompleted && (
                                      <Button
                                        size="sm"
                                        onClick={() => openUpdateModal(itinerary, milestoneKey)}
                                        className="bg-blue-600 hover:bg-blue-700 text-white"
                                        data-testid={`update-${milestoneKey}`}
                                      >
                                        Marcar
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Modal de actualización */}
        <Dialog open={updateModalOpen} onOpenChange={setUpdateModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="text-blue-600" />
                Actualizar Estado
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="font-medium text-blue-800">
                  {selectedMilestone && MILESTONE_LABELS[selectedMilestone]?.label}
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  {selectedMilestone && MILESTONE_LABELS[selectedMilestone]?.description}
                </p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Notas (opcional)</label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.toUpperCase())}
                  placeholder="Agregar comentarios sobre el estado..."
                  rows={3}
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setUpdateModalOpen(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleUpdateMilestone}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="confirm-milestone-update"
                >
                  Confirmar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DriverView;
